<template>
  <DataTable
      scrollable
      scroll-height="flex"
      :value="devicesIndexPageStore.getDevices"
      lazy
      paginator
      :first="devicesIndexPageStore.getFirstPage"
      :rows="devicesIndexPageStore.params.per_page"
      :rowsPerPageOptions="devicesIndexPageStore.pages"
      :total-records="devicesIndexPageStore.totalRecords"
      :loading="devicesIndexPageStore.loading"
      @page="devicesIndexPageStore.onPage($event)"
      @sort="devicesIndexPageStore.onSort($event)"
      @filter="devicesIndexPageStore.onFilter($event)"
      @update:rows="devicesIndexPageStore.onRowsUpdate"
      data-key="id"
      size="small"
      removable-sort
      always-show-paginator
      v-model:filters="devicesIndexPageStore.filters"
      filterDisplay="menu"
      :globalFilterFields="['device_type.name']"
  >
    <template #header>
      <Toolbar class="border-none shadow-none -m-3.5 rounded-none">
        <template #start>
          <Button v-if="$auth.hasPermission('create:devices')" icon="pi pi-plus" :label="$t('common.create')"
                  class="mr-2"
                  @click="createDevice"
                  data-cy="create-button">
            <template #icon="slotProps">
              <IconPlus class="w-4 h-4 mr-1" :class="slotProps.class"/>
            </template>
          </Button>
          <div class="flex-1">
            <IconField icon-position="left">
              <InputIcon>
                <IconSearch class="text-surface-400 dark:text-surface-600 h-4 w-4"/>
              </InputIcon>
              <InputText
                  :placeholder="$t('pages.devices.index.search_devices')"
                  class="sm:py-2"
                  v-model="devicesIndexPageStore.params.filter.search"
                  @keyup="devicesIndexPageStore.onSearch"
              />
            </IconField>
          </div>
        </template>

        <template #end>
          <div class="flex items-stretch justify-end space-x-2">
            <div class="flex-1 divide-x divide-slate-300 grid grid-cols-2">
              <IndexHeaderStat
                  title="Devices Total"
                  :value="devicesDashboardPageStore?.getDevicesTotalCount"
                  :loading="devicesDashboardPageStore?.getDevicesTotalCountLoading"
              />
              <IndexHeaderStat
                  title="Needing An Update"
                  :value="devicesDashboardPageStore?.getDevicesNeedingAnUpdateCount"
                  :loading="devicesDashboardPageStore?.getDevicesNeedingAnUpdateCountLoading"
              />
            </div>
            <Button text severity="secondary" @click="refreshStats(true)">
              <template #icon="{class: iconClass}">
                <IconRefresh :class="iconClass" class="w-5 h-5"/>
              </template>
            </Button>
          </div>
        </template>
      </Toolbar>
    </template>
    <Column field="id" header="ID" class="pl-6 lg:pl-8 w-12" sortable></Column>

    <Column header="&nbsp;" class="w-[6rem]">
      <template #body="{data}">
        <div class="h-20 aspect-square flex items-center justify-center">
          <img :src="getImageUrl(data)" alt=""/>
        </div>
      </template>
    </Column>

    <DeviceSlideOver ref="slideOver" @saved="devicesIndexPageStore.loadData"/>
    <Column
        sortable
        field="serial_number"
        class="whitespace-nowrap"
    >
      <template #header="{data}">
        <span class="px-2.5 py-1.5">Serial Number</span>
      </template>
      <template #body="{data}">
        <router-link :to="{ name: 'devices.show', params: { device: data.id } }">
          <Button type="button" :label="data.serial_number" text/>
        </router-link>
      </template>
    </Column>

    <Column
        field="company.name"
        header="Company"
        filterField="company_ids"
        :showFilterMatchModes="false"
        class="whitespace-nowrap"
    >
      <template #filter="{filterModel,filterCallback}">
        <MultiSelect
            v-model="filterModel.value"
            :options="companiesStore.getCompanyOptions"
            option-label="name"
            option-value="id"
            :placeholder="$t('common.any')"
            class="w-full"
            :max-selected-labels="2"
        />
      </template>
    </Column>

    <Column field="device_model.name" header="Model" filter-field="device_model_ids" :show-filter-match-modes="false"
            class="whitespace-nowrap">
      <template #filter="{filterModel,filterCallback}">
        <MultiSelect
            v-model="filterModel.value"
            :options="deviceModelsStore.getDeviceModelOptions"
            option-label="name"
            option-value="id"
            :placeholder="$t('common.any')"
            class="w-full"
            :max-selected-labels="2"
        />
      </template>
    </Column>

    <Column
        field="device_type.name"
        header="Type"
        filterField="device_type_ids"
        :showFilterMatchModes="false"
        class="whitespace-nowrap"
    >
      <template #filter="{filterModel,filterCallback}">
        <MultiSelect
            v-model="filterModel.value"
            :options="deviceTypesStore.getDeviceTypeOptions"
            option-label="name"
            option-value="id"
            :placeholder="$t('common.any')"
            class="w-full"
            :max-selected-labels="2"
        />
      </template>
    </Column>
    <Column
        field="device_revision.name"
        header="Revision"
        filterField="device_revision_ids"
        :showFilterMatchModes="false"
    >
      <template #filter="{filterModel,filterCallback}">
        <MultiSelect
            v-model="filterModel.value"
            :options="deviceRevisionsStore.getDeviceRevisionOptions"
            option-label="name"
            option-value="id"
            :placeholder="$t('common.any')"
            class="w-full"
            :max-selected-labels="2"
        />
      </template>
    </Column>
    <Column field="current_firmware.version_number" header="Firmware Version"/>
    <Column field="requires_update"
            header="Requires Update"
            filterField="requires_updates"
            :showFilterMatchModes="false"
            :filterMenuStyle="{ width: '14rem' }"
            style="min-width: 12rem"
    >
      <template #body="{ data }">
        <Tag :value="getUpdateStatusName(data.requires_update)" :severity="getUpdateStatus(data.requires_update)"/>
      </template>

      <template #filter="{filterModel,filterCallback}">
        <MultiSelect
            v-model="filterModel.value"
            :options="devicesIndexPageStore.requiresUpdateStatuses"
            option-label="name"
            option-value="value"
            :placeholder="$t('common.any')"
            class="w-full"
            :max-selected-labels="2"
        >
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <Tag :value="slotProps.option?.name" :severity="getUpdateStatus(slotProps.option?.value)"/>
            </div>
          </template>
        </MultiSelect>
      </template>

    </Column>
  </DataTable>

  <DeviceSlideOver ref="slideOver" @saved="devicesIndexPageStore.loadData"/>
</template>

<script>
import {useDevicesIndexPageStore} from "@/stores/pages/devicesIndexPage.js";
import {useDeviceModelsStore} from "@/stores/deviceModels.js";
import {useDeviceTypesStore} from "@/stores/deviceTypes.js";
import {useDeviceRevisionsStore} from "@/stores/deviceRevisions.js";
import {useCompaniesStore} from "@/stores/companies.js";
import {mapStores} from "pinia";
import DeviceSlideOver from "@/Slideovers/DeviceSlideOver.vue";
import {IconPlus, IconSearch, IconRefresh} from "@tabler/icons-vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import IndexHeaderStat from "@/Widgets/Cards/IndexHeaderStat.vue";
import {useDevicesDashboardPageStore} from "@/stores/pages/devicesDashboardPage.js";
import {resolveDeviceImageUrl} from "@/Composables/DeviceImage.js";

export default {
  name: "DevicesIndexView",
  computed: {
    ...mapStores(useDeviceModelsStore),
    ...mapStores(useDeviceTypesStore),
    ...mapStores(useDevicesIndexPageStore),
    ...mapStores(useDeviceRevisionsStore),
    ...mapStores(useCompaniesStore),
    ...mapStores(useDevicesDashboardPageStore),
  },
  components: {
    IndexHeaderStat,
    IconPlus,
    IconSearch,
    DeviceSlideOver,
    IconField,
    InputIcon,
    IconRefresh
  },
  methods: {
    editDevice(device) {
      this.$refs.slideOver.editDevice(device);
    },
    createDevice() {
      this.$refs.slideOver.createDevice();
    },
    onSave() {
      this.loadData();
      this.refreshStats(true);
    },
    getUpdateStatusName(status) {
      let name = 'Undefined';
      name = this.devicesIndexPageStore.requiresUpdateStatuses.filter(requiresUpdate => requiresUpdate.value === status);
      return name[0].name;
    },
    getUpdateStatus(status) {
      switch (status) {
        case 1:
          return 'danger';

        case 0:
          return 'success';
      }
      return null;
    },
    getImageUrl(device) {
      return resolveDeviceImageUrl(device, 'https://placehold.co/600x400')
    },
    refreshStats(forceRefresh = false) {
      this.devicesDashboardPageStore.fetchDevicesTotalCount({}, forceRefresh);
      this.devicesDashboardPageStore.fetchDevicesNeedingAnUpdate({}, forceRefresh);
    }
  },
  created() {
    this.devicesIndexPageStore.initFilters();
  },
  mounted() {
    this.devicesIndexPageStore.loadData();
    this.deviceModelsStore.indexDeviceModelOptions();
    this.deviceTypesStore.indexDeviceTypeOptions();
    this.deviceRevisionsStore.indexDeviceRevisionOptions();
    this.companiesStore.indexCompanyOptions();

    this.refreshStats();
  }
}
</script>
